import React, { useState, useEffect } from 'react';
import { executiveApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  CurrencyInr, TrendUp, TrendDown, ChartLine, ShieldCheck,
  Wallet, Receipt, WarningCircle, Scales, Clock, ChartBar,
  ArrowUp, ArrowDown, Target, Lightning, MagnifyingGlass, ArrowsClockwise,
} from '@phosphor-icons/react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, AreaChart, Area, Legend, LineChart, Line, ComposedChart,
} from 'recharts';

const COLORS = ['#0EA5E9', '#8B5CF6', '#10B981', '#F59E0B', '#F43F5E', '#06B6D4', '#F97316', '#EC4899', '#6366F1', '#14B8A6'];
const formatCurrency = (v) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(v || 0);
const formatCompact = (v) => {
  if (!v) return '0';
  if (v >= 10000000) return `${(v / 10000000).toFixed(1)}Cr`;
  if (v >= 100000) return `${(v / 100000).toFixed(1)}L`;
  if (v >= 1000) return `${(v / 1000).toFixed(1)}K`;
  return v.toString();
};
const formatStatus = (s) => s?.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') || '';

const KpiCard = ({ label, value, subValue, icon: Icon, color, bgColor, trend, trendColor }) => (
  <Card className={`${bgColor} border-0 shadow-sm overflow-hidden relative`} data-testid={`kpi-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <CardContent className="p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{label}</p>
          <p className={`text-2xl font-bold font-['Outfit'] mt-1 ${color}`}>{value}</p>
          {subValue && <p className="text-[10px] text-slate-400 mt-0.5">{subValue}</p>}
        </div>
        <div className="h-10 w-10 rounded-xl bg-white/80 flex items-center justify-center shadow-sm">
          <Icon className={`h-5 w-5 ${color}`} weight="duotone" />
        </div>
      </div>
      {trend !== undefined && trend !== 0 && (
        <div className={`flex items-center gap-1 mt-2 text-[10px] font-semibold ${trendColor || (trend > 0 ? 'text-emerald-600' : 'text-red-500')}`}>
          {trend > 0 ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
          {Math.abs(trend)}% vs last month
        </div>
      )}
    </CardContent>
  </Card>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-slate-900 text-white px-3 py-2 rounded-lg shadow-xl text-xs">
      <p className="font-semibold mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color }} />
          {p.name}: {typeof p.value === 'number' && p.value > 999 ? formatCurrency(p.value) : p.value}
        </p>
      ))}
    </div>
  );
};

const ExecutiveDashboardPage = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const loadData = async (fromDate, toDate) => {
    setLoading(true);
    try {
      const res = await executiveApi.dashboard('monthly', fromDate || dateFrom, toDate || dateTo);
      setData(res.data);
    } catch (e) { console.error('Executive dashboard failed:', e); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadData(); }, []);

  const handleSearch = () => { loadData(dateFrom, dateTo); };
  const handleClear = () => { setDateFrom(''); setDateTo(''); loadData('', ''); };

  if (loading) return (
    <div className="flex items-center justify-center h-64" data-testid="exec-dashboard-loading">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-500" />
    </div>
  );

  if (!data) return (
    <div className="text-center py-16 text-slate-500">
      <ChartBar className="h-12 w-12 mx-auto mb-3 text-slate-300" />
      <p>Failed to load executive dashboard data.</p>
    </div>
  );

  const { revenue_summary: rs, monthly_trends, payer_performance, denial_trends, status_distribution, top_claims, kpis } = data;
  const cm = rs?.current_month || {};
  const at = rs?.all_time || {};

  const statusData = (status_distribution || []).map((s, i) => ({
    name: formatStatus(s.status),
    value: s.count,
    amount: s.amount,
    color: COLORS[i % COLORS.length],
  }));

  return (
    <div className="space-y-5" data-testid="executive-dashboard-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-900 via-slate-900 to-slate-800 rounded-2xl p-6 text-white" data-testid="exec-dashboard-header">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold font-['Outfit']" data-testid="exec-dashboard-heading">Executive Dashboard</h1>
            <p className="text-sm text-slate-300 mt-0.5">Financial Analytics & Revenue Performance</p>
          </div>
          <div className="text-right hidden md:block">
            <p className="text-2xl font-bold font-['Outfit']">{new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
            <p className="text-[10px] text-slate-400 uppercase tracking-wider">{user?.tenant_name || 'All Hospitals'}</p>
          </div>
        </div>
      </div>

      {/* Date Filter */}
      <div className="flex items-center gap-3 flex-wrap" data-testid="exec-date-filter">
        <div className="flex items-center gap-2">
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">From</label>
          <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
            className="h-9 w-40 text-sm border-slate-300" data-testid="exec-date-from" />
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">To</label>
          <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
            className="h-9 w-40 text-sm border-slate-300" data-testid="exec-date-to" />
        </div>
        <Button size="sm" onClick={handleSearch} className="h-9 bg-sky-600 hover:bg-sky-700 text-white text-xs gap-1.5" data-testid="exec-search-btn">
          <MagnifyingGlass className="h-3.5 w-3.5" /> Search
        </Button>
        {(dateFrom || dateTo) && (
          <Button size="sm" variant="outline" onClick={handleClear} className="h-9 text-xs gap-1.5" data-testid="exec-clear-btn">
            <ArrowsClockwise className="h-3.5 w-3.5" /> Clear
          </Button>
        )}
      </div>

      {/* KPI Row 1 - Revenue */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard label="Total Billed" value={formatCurrency(cm.billed)} subValue={`All-time: ${formatCompact(at.billed)}`}
          icon={Receipt} color="text-sky-600" bgColor="bg-sky-50" trend={rs?.billed_change} />
        <KpiCard label="Total Collected" value={formatCurrency(cm.collected)} subValue={`All-time: ${formatCompact(at.collected)}`}
          icon={Wallet} color="text-emerald-600" bgColor="bg-emerald-50" trend={rs?.collected_change} />
        <KpiCard label="Outstanding" value={formatCurrency(cm.outstanding)} subValue={`All-time: ${formatCompact(at.outstanding)}`}
          icon={WarningCircle} color="text-rose-600" bgColor="bg-rose-50" />
        <KpiCard label="Deductions" value={formatCurrency(cm.deductions)}
          subValue={`Cases this month: ${cm.claims_count}`}
          icon={Scales} color="text-amber-600" bgColor="bg-amber-50" />
      </div>

      {/* KPI Row 2 - Rates */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <KpiCard label="Collection Rate" value={`${kpis?.collection_rate || 0}%`}
          icon={Target} color={kpis?.collection_rate >= 50 ? 'text-emerald-600' : 'text-amber-600'}
          bgColor={kpis?.collection_rate >= 50 ? 'bg-emerald-50' : 'bg-amber-50'} />
        <KpiCard label="Approval Rate" value={`${kpis?.approval_rate || 0}%`}
          icon={ShieldCheck} color="text-violet-600" bgColor="bg-violet-50" />
        <KpiCard label="Denial Rate" value={`${kpis?.denial_rate || 0}%`}
          icon={TrendDown} color={kpis?.denial_rate > 10 ? 'text-red-600' : 'text-emerald-600'}
          bgColor={kpis?.denial_rate > 10 ? 'bg-red-50' : 'bg-emerald-50'} />
        <KpiCard label="Avg Processing" value={`${data.avg_processing_days || 0} days`}
          icon={Clock} color="text-blue-600" bgColor="bg-blue-50" />
        <KpiCard label="Settlement Breaches" value={data.settlement_breaches || 0}
          icon={Lightning} color={data.settlement_breaches > 0 ? 'text-red-600' : 'text-emerald-600'}
          bgColor={data.settlement_breaches > 0 ? 'bg-red-50' : 'bg-emerald-50'} />
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Monthly Revenue Trends */}
        <Card className="bg-white border border-slate-200 shadow-sm lg:col-span-2" data-testid="monthly-revenue-chart">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <ChartLine className="h-4 w-4 text-sky-500" /> Monthly Revenue Trends
            </CardTitle>
          </CardHeader>
          <CardContent>
            {monthly_trends.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <ComposedChart data={monthly_trends}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} tickFormatter={formatCompact} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 10 }} />
                  <Bar dataKey="billed" fill="#0EA5E9" name="Billed" radius={[4, 4, 0, 0]} barSize={24} />
                  <Bar dataKey="collected" fill="#10B981" name="Collected" radius={[4, 4, 0, 0]} barSize={24} />
                  <Line dataKey="approved" stroke="#8B5CF6" name="Approved" strokeWidth={2} dot={{ fill: '#8B5CF6', r: 3 }} />
                </ComposedChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[260px] flex items-center justify-center text-slate-400 text-sm">No trend data yet</div>
            )}
          </CardContent>
        </Card>

        {/* Status Distribution */}
        <Card className="bg-white border border-slate-200 shadow-sm" data-testid="status-distribution-chart">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-slate-700">Claim Status Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            {statusData.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie data={statusData} cx="50%" cy="50%" innerRadius={55} outerRadius={85}
                    dataKey="value" nameKey="name" paddingAngle={2}>
                    {statusData.map((entry, index) => (
                      <Cell key={index} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value, name) => [value, name]} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[260px] flex items-center justify-center text-slate-400 text-sm">No data</div>
            )}
            <div className="flex flex-wrap gap-2 mt-1">
              {statusData.slice(0, 6).map((s, i) => (
                <div key={i} className="flex items-center gap-1 text-[9px]">
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: s.color }} />
                  <span className="text-slate-500">{s.name}: {s.value}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 2 - Denial Trends */}
      {denial_trends.length > 0 && (
        <Card className="bg-white border border-slate-200 shadow-sm" data-testid="denial-trends-chart">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <TrendDown className="h-4 w-4 text-rose-500" /> Denial Rate Trends
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={denial_trends}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} unit="%" />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="denial_rate" stroke="#F43F5E" fill="#FEE2E2" name="Denial Rate %" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Payer Performance Table */}
      <Card className="bg-white border border-slate-200 shadow-sm" data-testid="payer-performance-table">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
            <CurrencyInr className="h-4 w-4 text-emerald-500" /> Payer-wise Collection Performance
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Payer / TPA</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold text-right">Billed</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold text-right">Approved</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold text-right">Collected</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold text-right">Outstanding</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold text-center">Collection %</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold text-center">Denial %</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold text-center">Claims</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payer_performance.length > 0 ? payer_performance.map((p, i) => (
                  <TableRow key={i} className="hover:bg-sky-50/50">
                    <TableCell className="text-sm font-medium text-slate-800">{p.payer}</TableCell>
                    <TableCell className="text-sm text-right text-slate-600">{formatCurrency(p.billed)}</TableCell>
                    <TableCell className="text-sm text-right text-violet-600">{formatCurrency(p.approved)}</TableCell>
                    <TableCell className="text-sm text-right text-emerald-600 font-medium">{formatCurrency(p.collected)}</TableCell>
                    <TableCell className="text-sm text-right text-rose-600">{formatCurrency(p.outstanding)}</TableCell>
                    <TableCell className="text-center">
                      <Badge className={`${p.collection_rate >= 50 ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'} border-0 text-[10px]`}>
                        {p.collection_rate}%
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge className={`${p.denial_rate > 10 ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'} border-0 text-[10px]`}>
                        {p.denial_rate}%
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-center text-slate-500">{p.claims}</TableCell>
                  </TableRow>
                )) : (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-slate-400 text-sm">No payer data available</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Top Claims Table */}
      <Card className="bg-white border border-slate-200 shadow-sm" data-testid="top-claims-table">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
            <ChartBar className="h-4 w-4 text-indigo-500" /> Top 5 High-Value Cases
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Case No</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Claim No</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Patient</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Payer</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold text-right">Amount</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold text-center">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {top_claims.length > 0 ? top_claims.map((c, i) => (
                  <TableRow key={i} className="hover:bg-sky-50/50">
                    <TableCell className="text-sm font-medium text-sky-700">{c.case_no}</TableCell>
                    <TableCell className="text-sm text-slate-600">{c.claim_no || '-'}</TableCell>
                    <TableCell className="text-sm text-slate-800">{c.patient}</TableCell>
                    <TableCell className="text-sm text-orange-600">{c.payer || '-'}</TableCell>
                    <TableCell className="text-sm text-right font-semibold text-slate-800">{formatCurrency(c.amount)}</TableCell>
                    <TableCell className="text-center">
                      <Badge className="bg-slate-100 text-slate-600 border-0 text-[10px]">{formatStatus(c.status)}</Badge>
                    </TableCell>
                  </TableRow>
                )) : (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-slate-400 text-sm">No claims data</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ExecutiveDashboardPage;
